import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthContext";
import {
  Scissors, Wand2, Film, Sparkles, Users, Share2, ChevronRight, Play,
  Upload, Layers, MessageSquare, Eye, Heart, ZoomIn, Tags, Trophy,
  ArrowRight, Cpu, Globe, Calendar, Activity, Crosshair, Mic, Brush, Lightbulb,
  Database, Star, Image as ImageIcon, MapPin, LogIn,
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1723161134033-ece937266ae2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjb3dib3klMjBzdW5zZXQlMjBhcmVuYXxlbnwwfHx8fDE3NzgyODk2Mjd8MA&ixlib=rb-4.1.0&q=85";
const CLIP_BARREL = "https://images.unsplash.com/photo-1760041870271-496f3d0d46ee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxiYXJyZWwlMjByYWNpbmclMjByb2RlbyUyMGR1c3R8ZW58MHx8fHwxNzc4Mjg5NjI4fDA&ixlib=rb-4.1.0&q=85";
const TIMELINE_UI = "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHx2aWRlbyUyMHRpbWVsaW5lJTIwZWRpdGluZyUyMFVJfGVufDB8fHx8MTc3ODI4OTYyN3ww&ixlib=rb-4.1.0&q=85";

const REACTIONS = ["🔥", "👏", "❤️", "🤠", "🪢"];
const CATS = ["Barrel Racing", "Team Roping", "Tie-Down", "Steer Wrestling", "Breakaway"];

export default function LandingPage() {
  return (
    <div className="bg-stone-950 text-stone-100 min-h-screen overflow-x-hidden" style={{ fontFamily: "IBM Plex Sans, system-ui, sans-serif" }} data-testid="landing-page">
      <Navbar />
      <Hero />
      <Marquee />
      <HowItWorks />
      <AIPowerTrio />
      <FeatureBento />
      <AISpotlight />
      <RiderDashboardShowcase />
      <SharingShowcase />
      <FinalCTA />
      <Footer />
    </div>
  );
}

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const homeHref = user?.role === "client" ? "/portal" : "/dashboard";
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all ${scrolled ? "bg-stone-950/85 backdrop-blur-xl border-b border-stone-800" : "bg-transparent"}`} data-testid="landing-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-6">
        <Link to="/" className="flex items-baseline gap-2" data-testid="brand-link">
          <span className="text-orange-600 text-[10px] tracking-[0.3em] uppercase font-bold">Rodeo</span>
          <span className="font-bold text-stone-100 uppercase tracking-tight text-2xl leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Cut Studio</span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-xs uppercase tracking-[0.2em] text-stone-400 ml-10">
          <a href="#how" className="hover:text-stone-100 transition-colors">How it works</a>
          <a href="#power" className="hover:text-stone-100 transition-colors">AI Power Trio</a>
          <a href="#features" className="hover:text-stone-100 transition-colors">Features</a>
          <a href="#dashboard" className="hover:text-stone-100 transition-colors">Riders</a>
        </nav>
        <div className="ml-auto flex items-center gap-2">
          {user ? (
            <Link to={homeHref} className="btn-primary !text-xs inline-flex items-center gap-1.5" data-testid="nav-go-app">
              Open App <ChevronRight size={14} />
            </Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-flex btn-secondary !text-xs items-center gap-1.5" data-testid="nav-signin-btn">
                <LogIn size={12} /> Sign In
              </Link>
              <Link to="/login" className="btn-primary !text-xs inline-flex items-center gap-1.5" data-testid="nav-cta">
                Get Started <ChevronRight size={14} />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative pt-36 pb-24 overflow-hidden" data-testid="hero-section">
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-950/85 to-stone-950" />
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(circle at 20% 50%, rgba(234,88,12,0.18), transparent 50%), radial-gradient(circle at 80% 30%, rgba(234,88,12,0.08), transparent 60%)",
        }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 border border-orange-700/60 bg-orange-600/10 px-3 py-1 mb-6" data-testid="hero-eyebrow">
            <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-orange-400 font-bold">The Rodeo Video Engine</span>
          </div>
          <h1 className="font-display uppercase font-bold leading-[0.9] text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            <span className="block">Cut Runs.</span>
            <span className="block">Build Reels.</span>
            <span className="block text-orange-500">Grow Fans.</span>
          </h1>
          <p className="mt-7 text-stone-300 text-lg max-w-xl leading-relaxed">
            AI-powered video studio built for rodeo trainers, riders, and event organizers. Drop in 4 hours of arena footage. Walk out with labeled clips, polished branded reels, and rider dashboards your fans actually return to. Now with <span className="text-orange-400">visual rider tracking</span> and <span className="text-orange-400">audio-based name detection</span>.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/studio" className="btn-primary inline-flex items-center gap-2" style={{ boxShadow: "4px 4px 0px 0px #292524" }} data-testid="hero-cta-start">
              <Scissors size={14} /> Start Cutting
            </Link>
            <a href="#power" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-sample">
              <Play size={14} /> See AI in Action
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-12 grid grid-cols-3 gap-8 max-w-md">
            <Stat label="Hours / Reel" value="4 → 0:30" />
            <Stat label="AI passes" value="3-tier" />
            <Stat label="Disciplines" value="5" />
          </div>
        </div>

        {/* Right: floating mock collage */}
        <div className="lg:col-span-5 relative h-[480px] hidden lg:block">
          {/* Background card – timeline */}
          <div className="absolute top-0 right-0 w-[78%] aspect-video border border-stone-800 bg-stone-900 overflow-hidden rotate-[1.2deg] shadow-2xl">
            <img src={CLIP_BARREL} alt="" className="w-full h-full object-cover opacity-90" />
            <div className="absolute top-2 left-2"><span className="tag-cat backdrop-blur-sm bg-stone-900/80">Barrel Racing</span></div>
            <div className="absolute bottom-2 right-2 font-mono-tc text-xs text-orange-400 bg-black/70 px-2 py-0.5">14.2s</div>
          </div>
          {/* Foreground card – AI suggest */}
          <div className="absolute bottom-6 left-0 w-[68%] border border-stone-700 bg-stone-950/90 backdrop-blur-md shadow-[8px_8px_0_0_rgba(234,88,12,0.25)] -rotate-[1.5deg]">
            <div className="px-4 pt-3 pb-2 border-b border-stone-800 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.25em] text-orange-500 font-bold">AI Suggested · 7 cuts</div>
              <Wand2 size={13} className="text-orange-500" />
            </div>
            <div className="p-3 space-y-1.5">
              {[
                ["00:42", "clean run", "01:14"],
                ["02:18", "broke barrier", "02:34"],
                ["03:07", "no-time", "03:22"],
              ].map((row, i) => (
                <div key={i} className="grid grid-cols-[60px_1fr_60px] gap-2 items-center text-xs px-2 py-1.5 hover:bg-stone-900/60 cursor-pointer">
                  <span className="font-mono-tc text-orange-400">{row[0]}</span>
                  <span className="text-stone-200 truncate">{row[1]}</span>
                  <span className="font-mono-tc text-stone-500 text-right">{row[2]}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <div className="font-mono-tc text-[10px] text-stone-500">+ 4 more</div>
                <div className="ml-auto flex items-center gap-1 text-emerald-400 text-xs">
                  <span className="text-base leading-none">👍</span>
                  <span className="font-mono-tc">trained</span>
                </div>
              </div>
            </div>
          </div>
          {/* Floating reactions chip */}
          <div className="absolute top-32 right-2 border border-stone-700 bg-stone-950/95 backdrop-blur px-3 py-2 rotate-[3deg] shadow-xl">
            <div className="text-[9px] uppercase tracking-widest text-stone-500 mb-1">Fan Wall</div>
            <div className="flex items-center gap-1.5 text-sm">
              {REACTIONS.map((e, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <span>{e}</span>
                  <span className="font-mono-tc text-stone-300 text-xs">{[42, 18, 27, 9, 14][i]}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono-tc text-orange-500 text-2xl font-bold">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mt-1">{label}</div>
    </div>
  );
}

function Marquee() {
  return (
    <section className="border-y border-stone-800 bg-stone-950 overflow-hidden" data-testid="categories-marquee">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-wrap items-center gap-x-10 gap-y-3">
        <span className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-bold">Disciplines covered</span>
        {CATS.map((c) => (
          <span key={c} className="font-display uppercase font-semibold text-stone-300 tracking-tight text-lg sm:text-xl" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {c}
          </span>
        ))}
      </div>
    </section>
  );
}

function AIPowerTrio() {
  const cards = [
    {
      icon: <Wand2 size={20} />,
      tag: "AI Pass 01",
      title: "Quick AI Suggest",
      desc: "One Gemini 3 Pro call on a 24× compressed summary. Best for short reels (< 15 min) — fast first pass that gives you the rhythm of the source in 1–3 minutes.",
      bullets: ["Single-pass · ~1–3 min", "Few-shot from your 👍/👎", "Coaching tips injected into the prompt"],
      accent: "from-amber-600/30 to-transparent",
    },
    {
      icon: <Crosshair size={20} />,
      tag: "AI Pass 02",
      title: "Deep Scan",
      desc: "Splits long reels into 10-min windows and runs Gemini independently on each — no early-bias, no missed runs across the second half. Dramatic recall lift on 30+ min footage.",
      bullets: ["Window-by-window pass", "Auto-dedupe vs existing cuts", "5× the recall on long reels"],
      accent: "from-orange-600/35 to-transparent",
      headline: true,
    },
    {
      icon: <Mic size={20} />,
      tag: "AI Pass 03",
      title: "Identify Riders",
      desc: "Whisper transcribes the announcer track. Gemini parses the 12-second window before each cut — \"Up next, Sarah Johnson on Dusty\" → cut auto-named. Never overwrites manual entries.",
      bullets: ["Whisper · Gemini 2.5 Flash", "Lead-in window matching", "Auto-fills cuts AND existing clips"],
      accent: "from-emerald-600/30 to-transparent",
    },
  ];
  return (
    <section id="power" className="py-24 lg:py-32 border-t border-stone-800 relative" data-testid="ai-power-trio-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader eyebrow="The AI Power Trio" title={<>Three AI passes.<br /><span className="text-orange-500">Every rider found. Every name caught.</span></>} />
        <p className="text-stone-400 mt-6 max-w-2xl text-lg leading-relaxed">
          A single AI call misses runs in long reels. Our three-pass system layers visual scanning, deep window-by-window coverage, and announcer audio recognition — so the 4-hour Sunday tape comes out with cuts <em>and</em> names ready to publish.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-14">
          {cards.map((c, i) => (
            <div
              key={i}
              className={`group relative border ${c.headline ? "border-orange-700 md:scale-[1.02] md:-my-2" : "border-stone-800"} bg-stone-900/40 hover:bg-stone-900/70 hover:border-orange-700 p-7 transition-all overflow-hidden`}
              data-testid={`power-trio-card-${i}`}
            >
              <div className={`absolute -top-12 -right-12 w-48 h-48 rounded-full bg-gradient-radial ${c.accent} opacity-50 blur-3xl pointer-events-none`} style={{ background: `radial-gradient(circle, ${c.headline ? "rgba(234,88,12,0.35)" : "rgba(120,113,108,0.15)"}, transparent 70%)` }} />
              <div className="relative">
                <div className="flex items-center gap-2 text-orange-500 text-[10px] uppercase tracking-[0.25em] font-bold">
                  <span className="opacity-90">{c.icon}</span> {c.tag}
                </div>
                <h3 className="font-display uppercase font-bold text-3xl mt-3 leading-tight tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{c.title}</h3>
                <p className="text-stone-400 mt-3 leading-relaxed text-sm">{c.desc}</p>
                <ul className="mt-5 space-y-2">
                  {c.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-xs text-stone-300">
                      <ChevronRight size={12} className="text-orange-500 mt-0.5 shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
                {c.headline && (
                  <div className="mt-6 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-orange-400 font-bold border-t border-orange-700/40 pt-4 w-full">
                    <Star size={11} className="fill-orange-400" /> Recommended for 15+ min reels
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Visual Rider ID — separate spotlight */}
        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6 border border-stone-800 bg-stone-900/30 p-7 lg:p-9" data-testid="visual-rider-id-spotlight">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 text-orange-500 text-[10px] uppercase tracking-[0.25em] font-bold">
              <Crosshair size={14} /> Bonus · Visual Rider ID
            </div>
            <h3 className="font-display uppercase font-bold text-4xl mt-3 leading-tight tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Draw a box. <span className="text-orange-500">Find every appearance.</span></h3>
            <p className="text-stone-400 mt-4 leading-relaxed">
              Pause the video, drag a rectangle around the rider you want to track, and Gemini scans the full reel for every other appearance of that rider/horse pair — outfit, horse markings, posture. Auto-creates cuts pre-tagged with the rider's name.
            </p>
            <ul className="mt-5 space-y-2">
              {["Visual matching across full source", "Pre-fills rider name on every detected cut", "Pairs with Audio ID for double confirmation"].map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-stone-300">
                  <ChevronRight size={14} className="text-orange-500 mt-0.5 shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-stone-700 bg-stone-950 p-5 relative overflow-hidden">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3">Tracking Job · #34812</div>
              <div className="aspect-video bg-stone-900 border border-stone-800 relative">
                <img src={CLIP_BARREL} alt="" className="w-full h-full object-cover opacity-70" />
                {/* Mock bounding box */}
                <div className="absolute border-2 border-orange-500 shadow-[0_0_0_2px_rgba(234,88,12,0.3)]" style={{ left: "32%", top: "28%", width: "26%", height: "44%" }}>
                  <div className="absolute -top-6 left-0 bg-orange-600 text-white text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-mono-tc">Rider · 0.92</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="aspect-video bg-stone-900 border border-stone-800 relative">
                    <img src={CLIP_BARREL} alt="" className="w-full h-full object-cover opacity-50" />
                    <div className="absolute bottom-0.5 right-0.5 font-mono-tc text-[8px] bg-black/80 text-orange-400 px-1">{["02:14", "07:48", "23:11"][i]}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-emerald-400 font-mono-tc">→ 11 appearances · 14 cuts created</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    {
      n: "01",
      icon: <Upload size={22} />,
      title: "Drop the Footage",
      desc: "Direct-to-S3 multipart uploads handle 25GB+ files. Or paste a Vimeo / YouTube URL and we'll pull it down.",
      tag: "S3 · Vimeo · YouTube",
    },
    {
      n: "02",
      icon: <Wand2 size={22} />,
      title: "Cut by Hand or by AI",
      desc: "Mark IN/OUT with timeline zoom up to 25×. Or unleash three AI passes: Quick Suggest, Deep Scan window-by-window, and Identify Riders from announcer audio.",
      tag: "Manual · AI Suggest · Deep Scan · Audio ID",
    },
    {
      n: "03",
      icon: <Sparkles size={22} />,
      title: "Reel It & Share It",
      desc: "Pick clips, hit Build Reel — branded title cards (rider · event · date), cross-fade transitions, custom intro/outro, your watermark. Ship a public share link.",
      tag: "Templates · Title cards · Public link",
    },
  ];
  return (
    <section id="how" className="py-24 lg:py-32 relative" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader eyebrow="The Workflow" title={<>Three steps from <span className="text-orange-500">arena tape</span><br />to ready-to-post.</>} />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 mt-16">
          {steps.map((s, i) => (
            <div key={i} className={`relative px-7 py-9 border-stone-800 ${i === 0 ? "border-t border-l border-r" : "border-t border-r"} ${i === steps.length - 1 ? "" : "md:border-r"} md:border-b border-b group hover:bg-stone-900/40 transition-colors`} data-testid={`step-card-${i+1}`}>
              <div className="absolute top-0 left-0 w-1 h-full bg-orange-600 origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500" />
              <div className="font-mono-tc text-stone-700 text-7xl font-bold leading-none mb-6">{s.n}</div>
              <div className="text-orange-500 mb-4">{s.icon}</div>
              <h3 className="font-display uppercase text-3xl font-bold leading-tight tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{s.title}</h3>
              <p className="text-stone-400 mt-3 leading-relaxed">{s.desc}</p>
              <div className="mt-6 inline-block label-tiny text-orange-400">{s.tag}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeatureBento() {
  return (
    <section id="features" className="py-24 lg:py-32 relative" data-testid="features-bento-grid">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader eyebrow="Control Room" title={<>Every tool a rodeo<br />operator <span className="text-orange-500">actually needs.</span></>} />

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-14">
          {/* Reel Templates (col 2 row 2) — replaces old AI bento */}
          <BentoCard className="md:col-span-2 md:row-span-2 relative overflow-hidden" testId="feature-card-reel-templates">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-orange-600/15 blur-3xl" />
            <div className="relative">
              <FeatureHead icon={<Brush size={18} />} label="Reel Templates" title="Brand every reel automatically" />
              <p className="text-stone-400 mt-3 max-w-md">Custom intro & outro cards, animated lower-thirds, brand colors, watermark. Save a template once — every reel comes out fully branded with rider name + sub-label baked in.</p>

              <div className="mt-7 grid grid-cols-2 gap-3">
                <div className="border border-stone-800 p-3 relative overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <div className="font-display text-base font-bold text-stone-100 leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>ROYAL CROWN</div>
                      <div className="text-[8px] uppercase tracking-widest text-orange-400 mt-1">Steer Roping · Day 2</div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-orange-500" />
                  <div className="aspect-video" />
                  <div className="text-[9px] uppercase tracking-widest text-stone-500 mt-2">Intro card · auto-generated</div>
                </div>
                <div className="border border-stone-800 p-3 relative overflow-hidden">
                  <div className="aspect-video bg-stone-900 relative">
                    <div className="absolute bottom-2 left-2 right-12 bg-orange-600/85 px-2 py-1">
                      <div className="text-[8px] uppercase tracking-widest text-white font-bold leading-tight">Sarah Johnson</div>
                      <div className="text-[7px] uppercase tracking-widest text-orange-100 mt-0.5">Clean Run · 14.2s</div>
                    </div>
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-stone-500 mt-2">Lower-third · animated</div>
                </div>
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-orange-400 font-mono-tc">Cross-fade transitions · 1080p H.264 · S3-native</div>
            </div>
          </BentoCard>

          {/* Coaching Tips */}
          <BentoCard className="md:col-span-2" testId="feature-card-coaching-tips">
            <FeatureHead icon={<Lightbulb size={18} />} label="Coaching Tips" title="Steer the AI in plain English" />
            <p className="text-stone-400 mt-2 text-sm">Type rules like <em>"prefer cuts that include the box break"</em> or <em>"never tag a clip under 4 seconds"</em>. Tips inject directly into every Gemini prompt — global or per-discipline.</p>
            <div className="mt-3 space-y-1.5">
              {[
                ["barrel_racing", "Include the first horn before the run starts"],
                ["all", "Skip practice / rope-laying segments"],
                ["team_roping", "Both header and heeler should be visible"],
              ].map((t, i) => (
                <div key={i} className="border-l-2 border-orange-600 pl-3 text-xs">
                  <span className="text-orange-400 font-mono-tc text-[9px] uppercase tracking-widest">{t[0]}</span>
                  <div className="text-stone-300 mt-0.5">{t[1]}</div>
                </div>
              ))}
            </div>
          </BentoCard>

          {/* Library filter */}
          <BentoCard className="md:row-span-2" testId="feature-card-library">
            <FeatureHead icon={<Layers size={18} />} label="Library" title="Filter, search, edit" />
            <div className="mt-4 space-y-1.5">
              {CATS.map((c, i) => (
                <div key={c} className="flex items-center justify-between text-xs px-2 py-2 border-l-2 border-orange-600/0 hover:border-orange-500 hover:bg-stone-900/40 transition-all">
                  <span className="text-stone-300">{c}</span>
                  <span className="font-mono-tc text-stone-500">{[127, 86, 54, 31, 22][i]}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-stone-800 text-[10px] text-stone-500 uppercase tracking-widest">By rider · By event · CSV export</div>
          </BentoCard>

          {/* Timeline zoom (smaller) */}
          <BentoCard testId="feature-card-timeline">
            <FeatureHead icon={<ZoomIn size={18} />} label="Timeline Zoom" title="Frame-precise scrub" />
            <p className="text-stone-400 mt-2 text-sm">1× → 25× zoom · 0.25× → 2× speed · auto-scrolling playhead. Mark the pocket — first horn, broken barrier — to the frame.</p>
            <div className="mt-4 h-3 bg-stone-900 border border-stone-800 relative overflow-hidden">
              {[12, 28, 41, 56, 73, 89].map((p) => (
                <div key={p} className="absolute top-0 h-full w-0.5 bg-orange-500/80" style={{ left: `${p}%` }} />
              ))}
              <div className="absolute top-0 h-full bg-orange-600/30 border-l border-r border-orange-500" style={{ left: "30%", width: "12%" }} />
            </div>
          </BentoCard>

          {/* Auto-compression */}
          <BentoCard testId="feature-card-auto-compress">
            <FeatureHead icon={<MapPin size={18} />} label="Auto-Compression" title="5–7× smaller in one pass" />
            <p className="text-stone-400 mt-2 text-sm">Long jackpot tape from a phone hits 4 GB? Auto-compress kicks in on upload, re-encodes to 720p, swaps it on S3. Saves storage, speeds up every later step.</p>
          </BentoCard>

          {/* Hero clips & avatars */}
          <BentoCard testId="feature-card-hero-clips">
            <FeatureHead icon={<ImageIcon size={18} />} label="Rider Branding" title="Avatars + pinned hero clips" />
            <p className="text-stone-400 mt-2 text-sm">Upload a portrait, pin a signature run as the dashboard hero. Profile pages instantly look like a sponsor sheet.</p>
          </BentoCard>

          {/* URL fetch — surfaces from anywhere */}
          <BentoCard testId="feature-card-url-fetch">
            <FeatureHead icon={<Globe size={18} />} label="URL Fetch" title="From anywhere in the app" />
            <p className="text-stone-400 mt-2 text-sm">Paste a Vimeo, YouTube, or direct mp4 URL from the Dashboard, the Studio editor, or upload page — live progress bar, auto-S3 hand-off.</p>
          </BentoCard>

          {/* Events */}
          <BentoCard testId="feature-card-events">
            <FeatureHead icon={<Calendar size={18} />} label="Events" title="Tag + filter by jackpot" />
            <p className="text-stone-400 mt-2 text-sm">Every video stores its event name + date. Library and reels stay organized by show, not by upload time.</p>
          </BentoCard>

          {/* Data Migration */}
          <BentoCard testId="feature-card-data-migration">
            <FeatureHead icon={<Database size={18} />} label="Data Migration" title="Move your training" />
            <p className="text-stone-400 mt-2 text-sm">One-click export of every cut, tip, template, rider profile, and comment. Restore into a new environment — your "AI learning" never resets.</p>
          </BentoCard>

          {/* Reels output */}
          <BentoCard className="md:col-span-2" testId="feature-card-reels">
            <FeatureHead icon={<Sparkles size={18} />} label="Reels" title="Polished output, one click" />
            <p className="text-stone-400 mt-2 text-sm">Pick 2–N clips. Get a 1080p reel with cross-fades, per-clip title cards (rider · sub-label · event · date), and your watermark — all FFmpeg, all on your S3. Or hit "Best Runs" on a rider and ship a season highlight in 30 seconds.</p>
            <div className="mt-4 flex gap-1.5 items-center">
              {[1,2,3,4].map((i) => (
                <div key={i} className="h-10 flex-1 bg-stone-900 border border-stone-800 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 via-orange-600/5 to-transparent" style={{ animationDelay: `${i*0.2}s` }} />
                  <div className="absolute bottom-1 left-1 font-mono-tc text-[8px] text-orange-400">{`00:${10 + i*8}`}</div>
                </div>
              ))}
            </div>
          </BentoCard>
        </div>
      </div>
    </section>
  );
}

function BentoCard({ children, className = "", testId }) {
  return (
    <div className={`group border border-stone-800 bg-stone-900/40 hover:border-orange-700 hover:bg-stone-900/70 transition-all p-6 ${className}`} data-testid={testId}>
      {children}
    </div>
  );
}

function FeatureHead({ icon, label, title }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-orange-500 text-[10px] uppercase tracking-[0.25em] font-bold">
        <span className="opacity-90">{icon}</span> {label}
      </div>
      <div className="font-display uppercase font-bold text-2xl mt-2 leading-tight tracking-tight text-stone-100" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{title}</div>
    </div>
  );
}

function AISpotlight() {
  return (
    <section id="ai" className="py-24 lg:py-32 relative bg-stone-950" data-testid="ai-spotlight">
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: "linear-gradient(rgba(234,88,12,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(234,88,12,0.04) 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5">
          <SectionHeader eyebrow="Few-Shot Training" title={<>Train the AI<br />in <span className="text-orange-500">your style.</span></>} compact />
          <p className="text-stone-300 mt-6 leading-relaxed">A generic AI sees "horse moves." Your AI learns the difference between a clean run, a broken barrier, and a no-time the way <em>you</em> see it. Each thumbs-up locks in your standard.</p>
          <ul className="mt-7 space-y-3">
            {[
              "Manual cuts auto-flag as good examples",
              "👍 / 👎 feeds the next prompt as in-context examples",
              "Per-category recall — barrel ≠ team roping",
              "30 examples per category = noticeably 'trained' feel",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-stone-300">
                <span className="text-orange-500 mt-0.5"><ChevronRight size={14} /></span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: Mock training stats */}
        <div className="lg:col-span-7">
          <div className="border border-stone-800 bg-stone-900/40 p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="label-tiny text-orange-500">AI Training Progress</div>
                <div className="font-display uppercase text-2xl font-bold mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Per-category recall</div>
              </div>
              <Activity size={20} className="text-orange-500" />
            </div>
            <div className="space-y-4">
              {[
                { name: "Barrel Racing", good: 28, bad: 4, manual: 12 },
                { name: "Team Roping", good: 14, bad: 2, manual: 8 },
                { name: "Tie-Down", good: 9, bad: 1, manual: 5 },
                { name: "Steer Wrestling", good: 3, bad: 0, manual: 2 },
                { name: "Breakaway", good: 2, bad: 0, manual: 1 },
              ].map((row) => {
                const pct = Math.min(100, (row.good / 30) * 100);
                return (
                  <div key={row.name}>
                    <div className="flex items-baseline justify-between mb-1.5">
                      <span className="text-sm text-stone-200">{row.name}</span>
                      <span className="font-mono-tc text-xs text-stone-500">
                        <span className="text-emerald-400">{row.good}✓</span> · <span className="text-rose-400">{row.bad}✗</span> · {row.manual} manual
                      </span>
                    </div>
                    <div className="h-1.5 bg-stone-900 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-orange-700 to-orange-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-7 pt-5 border-t border-stone-800 text-[10px] text-stone-500 uppercase tracking-widest">Live counter from your library</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RiderDashboardShowcase() {
  return (
    <section id="dashboard" className="py-24 lg:py-32 border-t border-stone-800 relative" data-testid="rider-dashboard-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader eyebrow="Rider Engagement" title={<>Every rider gets a<br /><span className="text-orange-500">dashboard worth bookmarking.</span></>} />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: stats explanation */}
          <div className="lg:col-span-5 space-y-6">
            <BulletItem icon={<ImageIcon size={16} />} title="Avatar + pinned hero clip" desc="Upload a portrait, pin a signature run as the dashboard hero. The first thing fans see is a sponsor-quality intro." />
            <BulletItem icon={<Eye size={16} />} title="Real engagement metrics" desc="Views, reactions, comments, reels — counted per rider, surfaced as hero stats so the rider sees their own traction." />
            <BulletItem icon={<MessageSquare size={16} />} title="Open fan wall" desc="No login required. Fans drop comments and emoji reactions. Rider sees a live activity feed they can reply to." />
            <BulletItem icon={<Trophy size={16} />} title="Best-Runs auto-reel" desc="One-click — combine the rider's best 👍 cuts into a branded season highlight reel using your active template." />
            <BulletItem icon={<Share2 size={16} />} title="One-click public share" desc="Generate a /share/{token} link. No sidebar, no app exposure — just the rider's profile, fan-ready." />
          </div>

          {/* Right: dashboard mockup */}
          <div className="lg:col-span-7">
            <div className="border border-stone-800 bg-stone-900/30 overflow-hidden">
              {/* mock header */}
              <div className="px-5 pt-5 pb-3 border-b border-stone-800 flex items-center gap-4">
                {/* Avatar mock */}
                <div className="w-14 h-14 border-2 border-orange-600 bg-gradient-to-br from-orange-700 to-amber-900 flex items-center justify-center font-display text-xl font-bold text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>SJ</div>
                <div className="border-l-4 border-orange-600 pl-4">
                  <div className="label-tiny mb-1">Rider Dashboard</div>
                  <div className="font-display uppercase text-3xl font-bold leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Sarah Johnson</div>
                </div>
              </div>
              {/* Pinned hero clip mock */}
              <div className="aspect-[16/6] relative overflow-hidden border-b border-stone-800">
                <img src={CLIP_BARREL} alt="" className="w-full h-full object-cover opacity-90" />
                <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/30 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="bg-orange-600 text-white text-[9px] uppercase tracking-widest px-2 py-0.5 font-mono-tc font-bold">★ Pinned Hero</span>
                  <span className="bg-stone-900/80 backdrop-blur text-stone-200 text-[9px] uppercase tracking-widest px-2 py-0.5">Barrel · 14.2s</span>
                </div>
                <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                  <div>
                    <div className="font-display uppercase text-xl font-bold leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Clean Run · WPRA Finals</div>
                    <div className="text-[10px] uppercase tracking-widest text-orange-400 mt-1">14.07 · 1st place pocket</div>
                  </div>
                  <Play size={32} className="text-stone-100 fill-orange-500" />
                </div>
              </div>
              {/* hero stats */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-0 border-b border-stone-800">
                {[
                  { label: "Views", value: "2.4k" },
                  { label: "Reactions", value: "183", color: "text-rose-400" },
                  { label: "Comments", value: "47", color: "text-emerald-400" },
                  { label: "Clips", value: "62" },
                  { label: "Reels", value: "8", color: "text-orange-500" },
                  { label: "Events", value: "11" },
                ].map((s, i) => (
                  <div key={i} className={`px-4 py-4 ${i < 5 ? "border-r border-stone-800" : ""}`}>
                    <div className="text-[9px] uppercase tracking-widest text-stone-500">{s.label}</div>
                    <div className={`font-display text-2xl font-bold mt-1 ${s.color || "text-stone-100"}`} style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{s.value}</div>
                  </div>
                ))}
              </div>
              {/* fan wall preview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                <div className="p-5 border-r border-stone-800">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2"><Activity size={12} /> Fan Wall</div>
                  <div className="space-y-3">
                    {[
                      { name: "Coach M.", text: "That swing pace was money. Buy that horse some oats.", emoji: "💬", time: "2m" },
                      { name: "@dustyhat", text: "🔥 reacted to clean run", time: "4m" },
                      { name: "JenR", text: "What barrel pattern were you working at home?", emoji: "💬", time: "11m" },
                      { name: "@ropertown", text: "🤠 reacted to broke barrier", time: "18m" },
                    ].map((a, i) => (
                      <div key={i} className="text-xs">
                        <div className="flex items-center gap-2 text-stone-500 font-mono-tc text-[10px]">
                          <span className="text-orange-400">{a.name}</span>
                          <span className="ml-auto">{a.time}</span>
                        </div>
                        <div className="text-stone-200 mt-0.5 line-clamp-2">{a.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2"><Heart size={12} /> Reactions</div>
                  <div className="space-y-2.5">
                    {[
                      { e: "🔥", n: 87 },
                      { e: "👏", n: 41 },
                      { e: "🤠", n: 34 },
                      { e: "❤️", n: 18 },
                      { e: "🪢", n: 3 },
                    ].map((r) => (
                      <div key={r.e} className="flex items-center gap-3">
                        <span className="text-base">{r.e}</span>
                        <div className="flex-1 h-1.5 bg-stone-900 overflow-hidden">
                          <div className="h-full bg-orange-500" style={{ width: `${(r.n/87)*100}%` }} />
                        </div>
                        <span className="font-mono-tc text-xs text-stone-300 w-8 text-right">{r.n}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-4 border-t border-stone-800 flex items-center gap-2">
                    <Trophy size={14} className="text-emerald-400" />
                    <span className="font-display text-2xl font-bold text-emerald-400" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>14</span>
                    <span className="text-stone-300 text-sm">clean runs</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BulletItem({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-10 h-10 border border-stone-800 bg-stone-900/40 flex items-center justify-center text-orange-500">
        {icon}
      </div>
      <div>
        <div className="font-display uppercase font-bold text-xl tracking-tight text-stone-100" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{title}</div>
        <div className="text-stone-400 text-sm mt-1 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}

function SharingShowcase() {
  return (
    <section className="py-20 border-t border-stone-800" data-testid="sharing-showcase">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-0 border border-stone-800">
        {[
          { icon: <Globe size={20} />, label: "Public links", title: "No login required", desc: "Drop a URL in a text. Fans react instantly." },
          { icon: <Film size={20} />, label: "CSV + Reels", title: "Coach-ready exports", desc: "Hand off labeled clip metadata for training reviews." },
          { icon: <Users size={20} />, label: "Free for fans", title: "Open comments", desc: "Visitors leave a name (or stay anon) — ratings stick." },
        ].map((c, i) => (
          <div key={i} className={`px-7 py-9 ${i < 2 ? "md:border-r border-stone-800" : ""} ${i > 0 ? "border-t md:border-t-0 border-stone-800" : ""}`}>
            <div className="text-orange-500 mb-3">{c.icon}</div>
            <div className="label-tiny mb-1">{c.label}</div>
            <div className="font-display uppercase font-bold text-2xl tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{c.title}</div>
            <div className="text-stone-400 text-sm mt-2">{c.desc}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative py-32 border-t border-stone-800 overflow-hidden" data-testid="footer-cta-section">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url(${HERO_BG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }} />
      <div className="absolute inset-0 bg-gradient-to-b from-stone-950 via-stone-950/85 to-stone-950" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <h2 className="font-display uppercase font-bold leading-[0.85] tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif", fontSize: "clamp(3rem, 8vw, 9rem)" }}>
          <span className="block">Your arena.</span>
          <span className="block text-orange-500">Your highlights.</span>
        </h2>
        <p className="text-stone-300 text-lg mt-8 max-w-xl">
          Stop spending Sunday nights scrubbing through 4 hours of arena tape. Drop the file. Mark the runs. Ship the reels.
        </p>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/studio" className="btn-primary inline-flex items-center gap-2 text-base !px-8 !py-4" style={{ boxShadow: "6px 6px 0px 0px #292524" }} data-testid="footer-cta-btn">
            <Scissors size={16} /> Start Cutting <ArrowRight size={16} />
          </Link>
          <Link to="/library" className="btn-secondary inline-flex items-center gap-2 text-base !px-8 !py-4" data-testid="footer-cta-library">
            <Layers size={16} /> Browse Library
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-stone-800 py-10" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-baseline justify-between gap-6">
        <div className="flex items-baseline gap-2">
          <span className="text-orange-600 text-[10px] tracking-[0.3em] uppercase font-bold">Rodeo</span>
          <span className="font-bold text-stone-100 uppercase tracking-tight text-xl leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Cut Studio</span>
        </div>
        <div className="flex flex-wrap gap-6 text-xs uppercase tracking-[0.2em] text-stone-500">
          <Link to="/dashboard" className="hover:text-stone-200">Dashboard</Link>
          <Link to="/studio" className="hover:text-stone-200">Studio</Link>
          <Link to="/library" className="hover:text-stone-200">Library</Link>
          <Link to="/reels" className="hover:text-stone-200">Reels</Link>
          <Link to="/riders" className="hover:text-stone-200">Riders</Link>
          <Link to="/settings" className="hover:text-stone-200">Settings</Link>
        </div>
        <div className="text-[10px] text-stone-600 uppercase tracking-widest">Phase 2 · AI · Audio · Branding</div>
      </div>
    </footer>
  );
}

function SectionHeader({ eyebrow, title, compact = false }) {
  return (
    <div className={`border-l-4 border-orange-600 pl-5 ${compact ? "" : ""}`}>
      <div className="label-tiny mb-2">{eyebrow}</div>
      <h2 className={`font-display uppercase font-bold leading-[0.95] tracking-tight ${compact ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"}`} style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
        {title}
      </h2>
    </div>
  );
}
